import _ from "lodash";
import React, { useEffect, useRef } from "react";

// components
import Prompt from "./components/Prompt";

// third party
import classNames from "classnames";

// utils
import { checkScrollAtBottom, isApiSupportedInBrowser } from "Utils";

// scss
import classes from "./PromptList.scss";

const PromptList = props => {
  const {
    t,
    prompts,
    selectedPrompt,
    setSelectedPrompt,
    searchText,
    selectedSidebarItem,
    setSelectedSidebarItem,
    isScrollEventAllowed,
    setIsScrollEventAllowed,
  } = props;

  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const scrollContainer = scrollContainerRef?.current;
    if (!scrollContainer) return;

    scrollContainer.addEventListener("scroll", handleScroll);
    scrollContainer.addEventListener("scrollend", handleScrollEnd);

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      scrollContainer.removeEventListener("scrollend", handleScrollEnd);
    };
  }, [isScrollEventAllowed, prompts, selectedSidebarItem]);

  // while scrolling update the selected sidebar item
  const handleScroll = () => {
    if (isScrollEventAllowed && scrollContainerRef.current) {
      let jumpToId = null;

      // when scroll is at the top then always select the first sub category
      if (scrollContainerRef.current.scrollTop === 0) {
        jumpToId = _.find(prompts, { depth: 3 })?.id;
      } else if (checkScrollAtBottom(scrollContainerRef.current)) {
        jumpToId = _.findLast(prompts, { depth: 2 })?.id;
      } else {
        _.forEach(prompts, ({ id, depth }) => {
          const node = document.getElementById(`display-prompt-${id}`);
          if (node) {
            const { top: nodeTop } = node.getBoundingClientRect() || {};
            if (
              nodeTop <= scrollContainerRef.current.clientHeight * 0.75 &&
              nodeTop >= 0
            ) {
              if (selectedSidebarItem !== id && (depth === 2 || depth === 3)) {
                jumpToId = id;
              } else {
                jumpToId = null;
              }
            }
          }
        });
      }

      if (jumpToId) {
        setSelectedSidebarItem(jumpToId);
      }
    }

    // scrollend not supported in browsers like safari so manually calling handleScrollEnd after 200ms
    if (!isApiSupportedInBrowser("onscrollend")) {
      setTimeout(() => handleScrollEnd(), 200);
    }
  };

  const handleScrollEnd = () => {
    setIsScrollEventAllowed(true);
  };

  return (
    <div ref={scrollContainerRef} className={classes.container}>
      {_.map(prompts, (prompt, index) => {
        const { id: promptId, parent, label, depth, promptType } = prompt || {};

        const isDisplayPrompt = promptType === "display";

        // unique id for display prompt for scrolling
        const displayPromptId = `display-prompt-${promptId}`;

        const uniqueKey = `${promptId}-${parent}-${index}`;

        const promptDisplayTextClass = classNames({
          [classes.promptDisplayText]: true,
          ["text-label-2"]: depth === 2,
          ["text-label-1"]: depth === 3,
          [classes.depthThreePromptStyle]: depth === 3,
          [classes.depthTwoPromptStyle]: depth === 2,
        });

        return isDisplayPrompt ? (
          <div
            id={displayPromptId}
            key={uniqueKey}
            className={promptDisplayTextClass}
          >
            {label}
          </div>
        ) : (
          <Prompt
            key={uniqueKey}
            uniqueKey={uniqueKey}
            t={t}
            prompt={prompt}
            selectedPrompt={selectedPrompt}
            setSelectedPrompt={setSelectedPrompt}
            searchText={searchText}
          />
        );
      })}
    </div>
  );
};

export default PromptList;
